import PageHero from "@/components/PageHero";
import CTA from "@/components/CTA";
import { motion } from "framer-motion";
import {
  ClipboardList,
  Boxes,
  MessagesSquare,
  Receipt,
  Truck,
  ShieldCheck,
  Sparkles,
  FileText,
  FolderUp,
  Workflow,
  BellRing,
  BarChart3,
  MapPin,
  PackageCheck,
  Users,
  History,
} from "lucide-react";

const FEATURE_SECTIONS = [
  {
    title: "Case Intake",
    icon: ClipboardList,
    cards: [
      {
        title: "Digital Rx",
        description: "Structured Rx with tooth chart, materials, shade, and notes",
        icon: FileText,
      },
      {
        title: "File uploads",
        description: "STLs, images, and PDFs, versioned and secure.",
        icon: FolderUp,
      },
    ],
  },
  {
    title: "Production",
    icon: Boxes,
    cards: [
      {
        title: "Case tracking",
        description: "One live view of every case, filterable by status and stage.",
        icon: Workflow,
      },
      {
        title: "Production management (Coming Soon)",
        description: "Assign work across technicians and stations.",
        icon: Workflow,
      },
    ],
  },
  {
    title: "Communication",
    icon: MessagesSquare,
    cards: [
      {
        title: "Communication",
        description: "Every note stays on the case, in one shared thread.",
        icon: MessagesSquare,
      },
      {
        title: "Notifications",
        description: "Email notification configurable per role.",
        icon: BellRing,
      },
    ],
  },
  {
    title: "Money",
    icon: Receipt,
    cards: [
      {
        title: "Billing",
        description: "Auto-invoicing straight from the Rx.",
        icon: Receipt,
      },
      {
        title: "Reports",
        description: "Filter and share reports on turnaround and revenue.",
        icon: BarChart3,
      },
    ],
  },
  {
    title: "Delivery",
    icon: Truck,
    cards: [
      {
        title: "Drivers",
        description: "Location tracking and proof of delivery.",
        icon: MapPin,
      },
      {
        title: "Delivery",
        description: "Signature, timestamp, and photo for every drop-off.",
        icon: PackageCheck,
      },
    ],
  },
  {
    title: "Trust and Access",
    icon: ShieldCheck,
    cards: [
      {
        title: "User roles",
        description: "Separate seats for doctors, technicians, drivers, and admins.",
        icon: Users,
      },
      {
        title: "Audit logs",
        description: "Every action recorded, with who did it and when",
        icon: History,
      },
    ],
  },
  {
    title: "Coming soon",
    icon: Sparkles,
    cards: [
      {
        title: "Future integrations",
        description: "Scanners, CAD software, accounting, and CRM integrations.",
        icon: Sparkles,
      },
    ],
  },
];

export default function Features() {
  return (
    <>
      <PageHero
        chapter="§ 03"
        eyebrow="Features"
        title={"Connecting your case one feature at a time."}
        description="Every feature works with the others, from the moment a case starts to the moment it's delivered."
      />

      <section data-testid="features-blocks" className="relative pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 space-y-8 lg:space-y-10">
          {FEATURE_SECTIONS.map((section, i) => {
            const SectionIcon = section.icon;
            return (
              <motion.div
                key={section.title}
                data-testid={`feature-section-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.02 }}
                className="rounded-3xl hairline glass p-6 lg:p-8"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl hairline inline-flex items-center justify-center">
                    <SectionIcon className="h-4 w-4 text-brand-plum" />
                  </div>
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                      Section {String(i + 1).padStart(2, "0")}
                    </p>
                    <h2 className="mt-1 font-heading text-2xl sm:text-3xl font-semibold tracking-[-0.02em] leading-[1.1]">
                      {section.title}
                    </h2>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {section.cards.map((card) => {
                    const CardIcon = card.icon;
                    return (
                      <article
                        key={card.title}
                        className="rounded-2xl hairline bg-background/70 p-5 sm:p-6"
                      >
                        <div className="h-9 w-9 rounded-lg hairline inline-flex items-center justify-center">
                          <CardIcon className="h-4 w-4 text-brand-plum" />
                        </div>
                        <h3 className="mt-4 font-heading text-xl font-semibold tracking-tight">
                          {card.title}
                        </h3>
                        <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">
                          {card.description}
                        </p>
                      </article>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <CTA />
    </>
  );
}
